import React from "react";
import RangeSelector from "./RangeSelector";
import { Bot, Strategy } from "../types/bot.type";
import { setCustomTradeStrategies } from "../lib/firebase";
import HelpButton from "./HelpButton";

interface SellConfigProps {
  bot: Bot;
  updateBot?: (bot: Bot) => void;
  trade?: any;
}

const SellConfig: React.FC<SellConfigProps> = ({ bot, updateBot, trade }) => {
  const strategies: Strategy[] =
    (bot?.data?.customTradeStrategies &&
      bot?.data?.customTradeStrategies[trade?.id]) ||
    bot.tradeParameters.sell.strategies;

  const handleStrategyChange = (
    strategyName: string,
    value: number,
    isEnabled: boolean
  ) => {
    const newStrategies: Strategy[] = strategies.filter(
      s => s.name !== strategyName
    );
    if (isEnabled) {
      newStrategies.push({
        name: strategyName,
        operator: "or",
        threshold: value
      });
    }
    if (!trade) {
      if (updateBot) {
        updateBot({
          ...bot,
          tradeParameters: {
            ...bot.tradeParameters,
            sell: {
              ...bot.tradeParameters.sell,
              strategies: newStrategies
            }
          }
        });
      }
    } else {
      setCustomTradeStrategies(bot.id, trade.id, newStrategies);
    }
  };

  const toggleStrategyEnabled = (
    strategyName: string,
    currentValue: number
  ) => {
    const isEnabled = !strategies.some(s => s.name === strategyName);
    handleStrategyChange(strategyName, currentValue, isEnabled);
  };

  const isStrategyEnabled = (name: string) =>
    strategies.some(s => s.name === name);
  const getStrategyValue = (name: string, defaultValue: number) =>
    strategies.find(s => s.name === name)?.threshold || defaultValue;

  return (
    <>
      {trade && (
        <div className="flex items-center justify-between pt-2 px-8 font-semibold text-xl">
          <span>Custom Trade Strategies</span>
          <HelpButton id="custom_trade_strategies" />
        </div>
      )}
      <RangeSelector
        title="Profit Margin"
        min={0}
        max={10}
        step={0.25}
        unit="%"
        value={getStrategyValue("profitMargin", 1)}
        onValueChange={value =>
          handleStrategyChange("profitMargin", value, true)
        }
        isEnabled={isStrategyEnabled("profitMargin")}
        toggleEnabled={() =>
          toggleStrategyEnabled(
            "profitMargin",
            getStrategyValue("profitMargin", 1)
          )
        }
        helpId="sell_profit_margin"
      />
      <RangeSelector
        title="Stop Loss"
        min={-10}
        max={0}
        step={0.25}
        unit="%"
        value={getStrategyValue("stopLoss", -5)}
        onValueChange={value => handleStrategyChange("stopLoss", value, true)}
        isEnabled={isStrategyEnabled("stopLoss")}
        toggleEnabled={() =>
          toggleStrategyEnabled("stopLoss", getStrategyValue("stopLoss", -5))
        }
        isStopLoss={true}
        helpId="sell_stop_loss"
      />
      <RangeSelector
        title="Relative Strength Index (RSI)"
        min={0}
        max={100}
        step={1}
        value={getStrategyValue("rsi", 70)}
        onValueChange={value => handleStrategyChange("rsi", value, true)}
        isEnabled={isStrategyEnabled("rsi")}
        toggleEnabled={() =>
          toggleStrategyEnabled("rsi", getStrategyValue("rsi", 30))
        }
        helpId="sell_rsi"
      />
    </>
  );
};

export default SellConfig;
